import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  locale?: 'ko_KR' | 'en_US';
  noindex?: boolean;
  structuredData?: object;
}

const BASE_URL = 'https://www.helistens.me';
const DEFAULT_IMAGE = `${BASE_URL}/images/ogimage.png`;

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  locale = 'ko_KR',
  noindex = false,
  structuredData,
}) => {
  const siteTitle = 'He Listens to Me';
  const fullTitle = title ? `${title} | ${siteTitle}` : `${siteTitle} | 맞춤형 온라인 교회`;
  
  const defaultDescription = locale === 'ko_KR'
    ? '당신의 기도에 귀 기울이는 맞춤형 기독교 예배 서비스. AI 기반 개인화된 설교, 찬송가, 성경 말씀을 경험하세요.'
    : 'Personalized Christian worship service that listens to your prayers. Experience AI-powered personalized sermons, hymns, and scripture.';
  
  const defaultKeywords = locale === 'ko_KR'
    ? '기독교, 예배, 기도, 개인 예배, 음성 기도, 맞춤 예배, 온라인 예배, 온라인 교회, 인터넷 교회, AI 예배, 맞춤형 설교'
    : 'Christian, worship, prayer, personal worship, voice prayer, customized worship, online worship, online church, internet church, AI worship, personalized sermon';

  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;
  const canonicalUrl = url ? `${BASE_URL}${url}` : BASE_URL;
  const alternateLocale = locale === 'ko_KR' ? 'en_US' : 'ko_KR';

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content="He Listens to Me Team" />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Language Alternates */}
      <link rel="alternate" hrefLang="ko" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content={locale} />
      <meta property="og:locale:alternate" content={alternateLocale} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={image} />
      
      {/* Mobile Optimization */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={siteTitle} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

// Pre-defined structured data for common page types
export const getHomePageStructuredData = (language: 'ko' | 'en' = 'ko') => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'He Listens to Me',
  alternateName: language === 'ko' ? '맞춤형 온라인 교회' : 'Personalized Online Church',
  url: 'https://www.helistens.me',
  description: language === 'ko' 
    ? '당신의 기도에 귀 기울이는 맞춤형 기독교 예배 서비스'
    : 'Personalized Christian worship service that listens to your prayers',
  inLanguage: language === 'ko' ? 'ko-KR' : 'en-US',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://www.helistens.me/worship?q={search_term_string}',
    },
    'query-input': 'required name=search_term_string',
  },
});

export const getChurchOrganizationData = (language: 'ko' | 'en' = 'ko') => ({
  '@context': 'https://schema.org',
  '@type': 'ReligiousOrganization',
  name: 'He Listens to Me',
  alternateName: language === 'ko' ? '맞춤형 온라인 교회' : 'Personalized Online Church',
  url: 'https://www.helistens.me',
  logo: 'https://www.helistens.me/images/logo.png',
  description: language === 'ko'
    ? 'AI 기술을 활용한 개인 맞춤형 기독교 예배 서비스를 제공합니다.'
    : 'We provide personalized Christian worship services using AI technology.',
  sameAs: [],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'support@helistens.me',
    contactType: 'customer service',
    availableLanguage: ['Korean', 'English'],
  },
});

export const getWorshipServiceData = (language: 'ko' | 'en' = 'ko') => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: language === 'ko' ? '개인 맞춤 예배' : 'Personalized Worship Service',
  description: language === 'ko'
    ? '당신의 기도 내용을 분석하여 맞춤형 성경 말씀, 찬송가, 설교를 제공하는 AI 기반 예배 서비스'
    : 'AI-powered worship service that analyzes your prayers to provide personalized scripture, hymns, and sermons',
  provider: {
    '@type': 'ReligiousOrganization',
    name: 'He Listens to Me',
    url: 'https://www.helistens.me',
  },
  serviceType: language === 'ko' ? '온라인 예배' : 'Online Worship',
  areaServed: {
    '@type': 'Place',
    name: 'Worldwide',
  },
  availableChannel: {
    '@type': 'ServiceChannel',
    serviceUrl: 'https://www.helistens.me/worship',
    serviceType: 'Online',
  },
});

export const getBreadcrumbData = (items: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `https://www.helistens.me${item.url}`,
  })),
});
